import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Row, Col, Card, CardBody, Badge, Button, Spinner, Alert,
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import * as moment from "moment";
import {
  loadDashboardStats, PENDING_SOURCES, DAILY_SOURCES,
} from "./dashboardStats";

/** Para bicimi (EUR) */
const paraYaz = (tutar) =>
  `${Number(tutar || 0).toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} €`;

/**
 * Yonetim ozeti: onay bekleyenler + satin alma/gelir + gunluk yeni kayitlar.
 * Veriler dogrudan OData'dan geliyor.
 */
const OperationsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStats(await loadDashboardStats(7));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !stats) {
    return (
      <Card>
        <CardBody className="text-center py-5">
          <Spinner color="primary" />
          <p className="text-muted mt-3 mb-0">İstatistikler yükleniyor...</p>
        </CardBody>
      </Card>
    );
  }
  if (!stats) return null;

  const totalPending = PENDING_SOURCES.reduce(
    (sum, s) => sum + (stats.pending[s.key] || 0), 0
  );
  const totalToday = DAILY_SOURCES.reduce(
    (sum, s) => sum + (stats.daily[s.key] || 0), 0
  );

  const sa = stats.satinAlma;

  const chartOptions = {
    chart: { toolbar: { show: false }, stacked: false },
    colors: DAILY_SOURCES.map(s => s.color),
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    xaxis: {
      categories: stats.days.map(d => moment(d).format("DD MMM")),
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: { labels: { formatter: v => Math.round(v) } },
    legend: { position: "top", horizontalAlign: "right" },
    grid: { borderColor: "#f1f1f1" },
    tooltip: { shared: true, intersect: false },
  };
  const chartSeries = DAILY_SOURCES.map(s => ({
    name: s.label,
    data: stats.series[s.key] || [],
  }));

  return (
    <React.Fragment>
      {stats.errors && stats.errors.length > 0 && (
        <Alert color="warning">
          Bazı veriler alınamadı: {stats.errors.join(", ")}. Sayıların bir kısmı eksik olabilir.
        </Alert>
      )}

      {/* --- ONAY BEKLEYENLER --- */}
      <Row>
        <Col lg="12">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
                <h5 className="bes-bolum-baslik flex-grow-1">
                  Onay Bekleyenler{" "}
                  <Badge color={totalPending > 0 ? "danger" : "success"} className="ms-1">
                    {totalPending}
                  </Badge>
                </h5>
                <Button color="light" size="sm" onClick={load} disabled={loading}>
                  <i className="bx bx-refresh align-middle me-1" />Yenile
                </Button>
                <Link to="/moderation" className="btn btn-sm btn-primary">
                  Onay Ekranına Git
                </Link>
              </div>

              <Row>
                {PENDING_SOURCES.map((s) => {
                  const n = stats.pending[s.key];
                  return (
                    <Col md="4" xl="3" sm="6" key={s.key} className="mb-3">
                      <Link
                        to="/moderation"
                        className={"bes-kutu" + (n > 0 ? " bes-kutu--dolu" : "")}
                      >
                        <div className="d-flex align-items-center">
                          <span className={"bes-kutu__ikon bg-soft-" + s.color + " text-" + s.color}>
                            <i className={"bx " + s.icon} />
                          </span>
                          <div className="ms-2">
                            <div className="bes-kutu__deger">{n === null ? "—" : n}</div>
                            <p className="bes-kutu__etiket">{s.label}</p>
                          </div>
                        </div>
                      </Link>
                    </Col>
                  );
                })}
                <Col md="4" xl="3" sm="6" className="mb-3">
                  <div className="bes-kutu bes-kutu--toplam">
                    <div className="d-flex align-items-center">
                      <span className="bes-kutu__ikon bg-soft-dark text-dark">
                        <i className="bx bx-list-check" />
                      </span>
                      <div className="ms-2">
                        <div className="bes-kutu__deger">{totalPending}</div>
                        <p className="bes-kutu__etiket">Toplam</p>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* --- SATIN ALMA / GELİR ---
           Panelin ana sayfasinda gelirle ilgili hicbir gosterge yoktu.
           Isletme acisindan en onemli rakam burasi. "Suresi dolmus"
           sayisi dogrudan bir yenileme (satis) firsati listesidir. */}
      {sa && (
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
                  <h5 className="bes-bolum-baslik flex-grow-1">
                    Satın Almalar{" "}
                    <span className="text-muted font-size-12">
                      (öne çıkan ilan + rehber reklamı)
                    </span>
                  </h5>
                  <Link to="/satin-alma-raporu" className="btn btn-sm btn-primary">
                    Detaylı Rapor
                  </Link>
                </div>

                <Row className="g-3">
                  <Col md="3" sm="6">
                    <div className="bes-kutu text-center">
                      <div className="bes-kutu__deger text-primary">{sa.toplam}</div>
                      <p className="bes-kutu__etiket">Toplam Satın Alma</p>
                    </div>
                  </Col>
                  <Col md="3" sm="6">
                    <div className="bes-kutu text-center">
                      <div className="bes-kutu__deger text-success">{sa.aktif}</div>
                      <p className="bes-kutu__etiket">Şu An Aktif</p>
                    </div>
                  </Col>
                  <Col md="3" sm="6">
                    <Link
                      to="/satin-alma-raporu"
                      className={"bes-kutu text-center" + (sa.dolmus > 0 ? " bes-kutu--dolu" : "")}
                    >
                      <div className="bes-kutu__deger text-warning">{sa.dolmus}</div>
                      <p className="bes-kutu__etiket">
                        Süresi Dolmuş <i className="bx bx-right-arrow-alt align-middle" />
                      </p>
                    </Link>
                  </Col>
                  <Col md="3" sm="6">
                    <div className="bes-kutu text-center">
                      <div className="bes-kutu__deger text-info">
                        {sa.birimFiyat == null ? "—" : paraYaz(sa.toplam * sa.birimFiyat)}
                      </div>
                      <p className="bes-kutu__etiket">Tahmini Toplam Gelir</p>
                    </div>
                  </Col>
                </Row>

                <p className="text-muted mb-0 font-size-11">
                  <i className="bx bx-info-circle align-middle me-1" />
                  Gelir tahminidir (birim fiyat × adet); mağaza komisyonu ve
                  iadeler dâhil değildir.
                  {sa.dolmus > 0 && (
                    <>
                      {" "}<strong>{sa.dolmus}</strong> müşterinin reklam süresi
                      dolmuş — yenileme için aranabilir.
                    </>
                  )}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* --- BUGÜN EKLENENLER --- */}
      <Row>
        <Col lg="12">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <h5 className="bes-bolum-baslik flex-grow-1">
                  Bugün Eklenenler{" "}
                  <span className="text-muted font-size-12">
                    ({moment().format("DD MMMM YYYY")})
                  </span>
                </h5>
                <Badge color="primary">{totalToday}</Badge>
              </div>

              <Row className="g-3">
                {DAILY_SOURCES.map((s) => (
                  <Col md="3" sm="6" key={s.key}>
                    <div className="bes-kutu text-center">
                      <div className="bes-kutu__deger" style={{ color: s.color }}>
                        {stats.daily[s.key] === null ? "—" : stats.daily[s.key]}
                      </div>
                      <p className="bes-kutu__etiket">{s.label}</p>
                    </div>
                  </Col>
                ))}
              </Row>

              <div className="mt-3">
                <h5 className="card-title font-size-14 mb-2">Son 7 Gün</h5>
                <ReactApexChart
                  options={chartOptions}
                  series={chartSeries}
                  type="line"
                  height={260}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default OperationsPanel;
